import React from 'react';
import { StageSection, Prose, KeyPoints, NumberedFlow } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Sequencing & Proving',
    tagline: 'A sequencer executes, a prover attests, Ethereum only has to check.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what a sequencer does versus what a prover does
//   Q1 - what an execution trace is and why it's the input to a STARK
export const blocks: StageBlock[] = [
    content('hero', () => (
        <StageSection gradientBorder eyebrow="Stage 6 of 8" title="Two off-chain jobs, one on-chain check">
            <Prose>
                Getting a batch of Starknet transactions onto Ethereum as a verified state update involves two
                distinct off-chain roles working in sequence, followed by one on-chain check:
            </Prose>
            <NumberedFlow steps={[
                ['Sequencer orders and executes', 'Collects pending transactions, decides their order, runs each one\'s __validate__ and __execute__, and produces a new Starknet state plus a full execution trace of every step taken.'],
                ['Prover builds a STARK', 'Consumes that execution trace and produces a succinct STARK proof attesting that the trace is a valid execution of the Cairo programs involved, under Starknet\'s rules.'],
                ['Ethereum verifies', 'An on-chain verifier contract checks the proof. If it\'s valid, the new state root is accepted as Starknet\'s current, final state.'],
            ]} />
        </StageSection>
    )),

    question(0),

    content('execution-trace', () => (
        <StageSection title="The execution trace is what actually gets proven">
            <Prose>
                As the sequencer runs Cairo bytecode, it records the exact sequence of machine states (register
                values, memory accesses, opcode executions) step by step. That record is the{' '}
                <strong>execution trace</strong>. A STARK proof is, at its core, a succinct argument that this trace
                is internally consistent and follows the rules of the Cairo machine: it doesn't re-describe the
                computation in a shorter form, it proves a claim about the trace's validity that a verifier can check
                far faster than redoing the computation itself.
            </Prose>
            <KeyPoints items={[
                <>The prover doesn't need to be trusted: anyone can independently recompute the trace and verify the proof matches it, and the proof only accepts genuinely valid traces.</>,
                <>Because verification is proof-based rather than re-execution, Ethereum's cost to accept an entire batch stays roughly constant no matter how much computation the batch actually contained.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('roles-can-differ', () => (
        <StageSection title="Sequencer and prover are roles, not necessarily one machine">
            <Prose>
                Nothing requires the same operator to run both jobs: a network can have a sequencer hand its output
                to a separate proving service, and multiple provers can compete to prove the same batch. Starknet's
                decentralization roadmap is largely about making both of these roles permissionless and distributed,
                rather than run by a single centralized operator as in Starknet's earlier phases.
            </Prose>
        </StageSection>
    )),
];
